import { useState, useEffect, useCallback, useMemo } from 'react';

export const useFetch = (fetchFn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchFn()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refetch: load };
};

export const useDarkMode = () => {
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);
  return [dark, () => setDark(d => !d)];
};

export const useSearch = (data, keys) => {
  const [query, setQuery] = useState('');
  const filtered = query ? data?.filter(item => keys.some(k => String(item[k] ?? '').toLowerCase().includes(query.toLowerCase()))) : data;
  return { query, setQuery, filtered: filtered || [] };
};

export const usePagination = (total, perPage = 10) => {
  const [page, setPage] = useState(1);
  const pages = Math.ceil(total / perPage);
  return { page, setPage, pages, perPage };
};

/**
 * useFilters — generic multi-field filter hook
 * @param {object} initial  — initial filter values e.g. { search:'', status:'', dateFrom:'', dateTo:'' }
 * @param {function} filterFn — (item, filters) => boolean
 * @param {array} data — raw data array
 */
export const useFilters = (initial, filterFn, data) => {
  const [filters, setFilters] = useState(initial);

  const onChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const onReset = useCallback(() => setFilters(initial), [initial]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter(item => filterFn(item, filters));
  }, [data, filters, filterFn]);

  const activeCount = Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined).length;

  return { filters, onChange, onReset, filtered, activeCount };
};
